import { useState, useEffect, useRef } from "react";
import { useAuth, API } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Upload, FileSpreadsheet, Check, X, Archive,
  Trash2, Clock, AlertCircle, CheckCircle2,
} from "lucide-react";

const STATUS_STYLES = {
  completed: { bg: "bg-green-500/10", text: "text-green-400", icon: CheckCircle2 },
  archived: { bg: "bg-white/[0.04]", text: "text-white/30", icon: Archive },
  processing: { bg: "bg-amber-500/10", text: "text-amber-400", icon: Clock },
  failed: { bg: "bg-red-500/10", text: "text-red-400", icon: AlertCircle },
};

function UploadModal({ token, onDone, onClose }) {
  const [file, setFile] = useState(null);
  const [sourceName, setSourceName] = useState("");
  const [batchName, setBatchName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const params = new URLSearchParams();
      if (sourceName.trim()) params.set("source_name", sourceName.trim());
      if (batchName.trim()) params.set("batch_name", batchName.trim());
      const res = await axios.post(`${API}/imports/upload?${params}`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success(`Import complete: ${res.data.hotels_imported} new, ${res.data.hotels_merged} merged`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    }
    setUploading(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="upload-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-[#111] border border-white/[0.08] rounded-2xl p-6 max-w-lg w-full shadow-2xl">
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-white text-lg font-semibold">Import Hotel Data</h2>
          <button onClick={onClose} className="text-white/30 hover:text-white/60"><X className="w-5 h-5" /></button>
        </div>

        {!result ? (
          <>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-white/50 text-xs font-medium mb-1.5 block">Source Name</label>
                <input value={sourceName} onChange={e => setSourceName(e.target.value)} placeholder="e.g. Leading Hotels" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-lg px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30" data-testid="import-source-name" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-medium mb-1.5 block">Batch Name</label>
                <input value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="e.g. leading_hotels_2026" className="w-full bg-white/[0.04] border border-white/[0.08] text-white text-sm rounded-lg px-3 py-2.5 outline-none placeholder:text-white/20 focus:border-[#C9A96E]/30" data-testid="import-batch-name" />
              </div>
            </div>

            <div className="mb-5">
              <input ref={fileRef} type="file" accept=".csv,.tsv,.txt,.xlsx,.xls" className="hidden" onChange={e => setFile(e.target.files?.[0] || null)} data-testid="import-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                className={`w-full py-8 rounded-xl border-2 border-dashed flex flex-col items-center gap-2 transition-all ${file ? "border-[#C9A96E]/30 bg-[#C9A96E]/5" : "border-white/[0.08] hover:border-white/[0.15] hover:bg-white/[0.02]"}`}
                data-testid="import-file-btn"
              >
                <Upload className={`w-6 h-6 ${file ? "text-[#C9A96E]" : "text-white/20"}`} />
                {file ? (
                  <span className="text-sm text-[#C9A96E] font-medium">{file.name}</span>
                ) : (
                  <span className="text-sm text-white/30">CSV, TSV, or XLSX file</span>
                )}
              </button>
            </div>

            <div className="text-white/25 text-[11px] mb-5 leading-relaxed">
              Supported: CSV, TSV, XLSX. Expected columns: hotel_name, city, country, region, website, consortium, spa, golf, beach, ski, meeting_rooms, mice_capable, category
            </div>

            <button onClick={handleUpload} disabled={!file || uploading} className="w-full py-3 bg-[#C9A96E] text-white rounded-xl font-medium text-sm hover:bg-[#C9A96E]/90 transition-colors disabled:opacity-30 flex items-center justify-center gap-2" data-testid="import-submit-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? "Processing..." : "Upload & Import"}
            </button>
          </>
        ) : (
          <div data-testid="import-result">
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-4 text-center">
                <div className="text-green-400 text-2xl font-bold font-mono" data-testid="import-count-imported">{result.hotels_imported}</div>
                <div className="text-green-400/60 text-xs mt-1">Imported</div>
              </div>
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 text-center">
                <div className="text-blue-400 text-2xl font-bold font-mono" data-testid="import-count-merged">{result.hotels_merged}</div>
                <div className="text-blue-400/60 text-xs mt-1">Merged</div>
              </div>
              <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-4 text-center">
                <div className="text-amber-400 text-2xl font-bold font-mono" data-testid="import-count-skipped">{result.duplicates_skipped}</div>
                <div className="text-amber-400/60 text-xs mt-1">Skipped</div>
              </div>
              <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-4 text-center">
                <div className="text-red-400 text-2xl font-bold font-mono" data-testid="import-count-errors">{result.import_errors}</div>
                <div className="text-red-400/60 text-xs mt-1">Errors</div>
              </div>
            </div>

            <div className="text-white/40 text-xs mb-3">
              Batch: <span className="text-white/60 font-mono">{result.batch_name}</span> · {result.total_rows} rows processed
            </div>

            {/* Parse Diagnostics */}
            {result.parse_info && (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 mb-3 space-y-1.5" data-testid="import-diagnostics">
                <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold">Parser Diagnostics</div>
                <div className="text-white/35 text-xs">
                  {result.parse_info.sheet_name && <span>Sheet: <span className="text-white/50 font-mono">{result.parse_info.sheet_name}</span> · </span>}
                  Format: <span className="text-white/50 font-mono">{result.parse_info.format}</span> ·{" "}
                  Header row: <span className="text-white/50 font-mono">{result.parse_info.header_row_index}</span> ·{" "}
                  Raw rows: <span className="text-white/50 font-mono">{result.parse_info.total_raw_rows}</span>
                </div>
              </div>
            )}

            {/* Mapped Columns */}
            {result.mapped_columns && Object.keys(result.mapped_columns).length > 0 && (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 mb-3" data-testid="import-columns">
                <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold mb-1.5">Mapped Columns</div>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(result.mapped_columns).map(([src, target]) => (
                    <span key={src} className="text-[10px] px-1.5 py-0.5 rounded bg-green-500/5 text-green-400/50">
                      {src} <span className="text-white/15">-&gt;</span> {target}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Preview Rows */}
            {result.preview_rows?.length > 0 && (
              <div className="bg-white/[0.02] border border-white/[0.06] rounded-lg p-3 mb-3" data-testid="import-preview">
                <div className="text-white/20 text-[10px] uppercase tracking-wider font-semibold mb-1.5">Preview (first rows)</div>
                {result.preview_rows.map(p => (
                  <div key={p.row} className="text-xs text-white/30 mb-1 font-mono">
                    Row {p.row}: {p.mapped.hotel_name ? <span className="text-white/50">{p.mapped.hotel_name}</span> : <span className="text-red-400/40">no name</span>}
                    {p.mapped.city && <span className="ml-1 text-white/25">{p.mapped.city}</span>}
                    {p.mapped.country && <span className="ml-1 text-white/20">{p.mapped.country}</span>}
                  </div>
                ))}
              </div>
            )}

            {/* Skip Reasons */}
            {result.skip_reasons?.length > 0 && (
              <div className="bg-amber-500/5 border border-amber-500/10 rounded-lg p-3 mb-3 max-h-28 overflow-auto" data-testid="import-skip-reasons">
                <div className="text-amber-400/50 text-[10px] uppercase tracking-wider font-semibold mb-1">Skip Reasons</div>
                {result.skip_reasons.map((r, i) => <div key={i} className="text-amber-400/40 text-[11px]">{r}</div>)}
              </div>
            )}

            {result.error_details?.length > 0 && (
              <div className="bg-red-500/5 border border-red-500/10 rounded-lg p-3 mb-3 max-h-28 overflow-auto">
                {result.error_details.map((e, i) => <div key={i} className="text-red-400/70 text-xs">{e}</div>)}
              </div>
            )}

            <button onClick={() => { onDone(); onClose(); }} className="w-full py-3 bg-[#C9A96E] text-white rounded-xl font-medium text-sm hover:bg-[#C9A96E]/90 transition-colors flex items-center justify-center gap-2" data-testid="import-done-btn">
              <Check className="w-4 h-4" /> Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ImportsPage() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpload, setShowUpload] = useState(false);
  const isAdmin = user?.role === "admin";

  const fetchImports = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/imports`, { headers: { Authorization: `Bearer ${token}` } });
      setImports(res.data);
    } catch { toast.error("Failed to load imports"); }
    setLoading(false);
  };

  useEffect(() => { fetchImports(); }, [token]);

  const archiveImport = async (id) => {
    try {
      await axios.put(`${API}/imports/${id}/archive`, {}, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Import archived");
      fetchImports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  const deleteImport = async (id) => {
    if (!window.confirm("Permanently delete this import record?")) return;
    try {
      await axios.delete(`${API}/imports/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Import deleted");
      fetchImports();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="p-4 lg:p-6" data-testid="imports-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl text-white" data-testid="imports-title">Imports</h1>
          <p className="text-white/40 text-sm mt-0.5">{imports.length} import batches</p>
        </div>
        <button
          onClick={() => setShowUpload(true)}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#C9A96E] text-white rounded-xl text-sm font-semibold hover:bg-[#C9A96E]/90 transition-colors"
          data-testid="new-import-btn"
        >
          <Upload className="w-4 h-4" /> New Import
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#C9A96E]" /></div>
      ) : imports.length === 0 ? (
        <div className="text-center py-20">
          <FileSpreadsheet className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/30 text-sm mb-2">No imports yet</p>
          <p className="text-white/15 text-xs">Upload a CSV, TSV, or XLSX file to get started</p>
        </div>
      ) : (
        <div className="space-y-3">
          {imports.map(imp => {
            const style = STATUS_STYLES[imp.processing_status] || STATUS_STYLES.completed;
            const StatusIcon = style.icon;
            return (
              <div key={imp.id} className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4 hover:bg-white/[0.03] transition-colors cursor-pointer" data-testid={`import-row-${imp.id}`} onClick={() => navigate(`/imports/${imp.id}`)}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className={`w-9 h-9 rounded-lg ${style.bg} flex items-center justify-center shrink-0`}>
                      <StatusIcon className={`w-4 h-4 ${style.text}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-white font-medium text-sm truncate">{imp.source_name || imp.batch_name}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded-full ${style.bg} ${style.text} font-medium`}>{imp.processing_status}</span>
                      </div>
                      <div className="text-white/25 text-xs space-y-0.5">
                        <div>
                          <span className="text-white/40 font-mono">{imp.batch_name}</span>
                          <span className="mx-1.5">·</span>
                          <span>{imp.file_name}</span>
                          <span className="mx-1.5">·</span>
                          <span className="uppercase text-white/20">{imp.file_type}</span>
                        </div>
                        <div>
                          {new Date(imp.upload_date).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" })}
                          {imp.uploaded_by && <span className="ml-1.5">by {imp.uploaded_by}</span>}
                          {imp.parse_info?.sheet_name && <span className="ml-1.5">· Sheet: <span className="font-mono text-white/30">{imp.parse_info.sheet_name}</span></span>}
                          {imp.parse_info?.header_row_index != null && <span className="ml-1.5">· Header: row {imp.parse_info.header_row_index}</span>}
                        </div>
                        {imp.mapped_columns && Object.keys(imp.mapped_columns).length > 0 && (
                          <div className="flex flex-wrap gap-0.5 mt-1">
                            {Object.entries(imp.mapped_columns).slice(0, 8).map(([src, tgt]) => (
                              <span key={src} className="text-[9px] px-1 py-0 rounded bg-white/[0.03] text-white/20">{src}</span>
                            ))}
                            {Object.keys(imp.mapped_columns).length > 8 && <span className="text-[9px] text-white/15">+{Object.keys(imp.mapped_columns).length - 8}</span>}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 shrink-0">
                    <div className="grid grid-cols-4 gap-3 text-center">
                      <div><div className="text-green-400 font-mono text-sm font-bold">{imp.imported}</div><div className="text-white/15 text-[9px]">New</div></div>
                      <div><div className="text-blue-400 font-mono text-sm font-bold">{imp.merged}</div><div className="text-white/15 text-[9px]">Merged</div></div>
                      <div><div className="text-amber-400 font-mono text-sm font-bold">{imp.skipped}</div><div className="text-white/15 text-[9px]">Skip</div></div>
                      <div><div className="text-red-400 font-mono text-sm font-bold">{imp.errors}</div><div className="text-white/15 text-[9px]">Errors</div></div>
                    </div>
                    {isAdmin && imp.processing_status !== "archived" && (
                      <button onClick={(e) => { e.stopPropagation(); archiveImport(imp.id); }} className="p-1.5 text-white/15 hover:text-white/40 transition-colors" title="Archive" data-testid={`archive-btn-${imp.id}`}>
                        <Archive className="w-4 h-4" />
                      </button>
                    )}
                    {isAdmin && (
                      <button onClick={(e) => { e.stopPropagation(); deleteImport(imp.id); }} className="p-1.5 text-white/15 hover:text-red-400/60 transition-colors" title="Delete" data-testid={`delete-btn-${imp.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showUpload && (
        <UploadModal token={token} onDone={fetchImports} onClose={() => setShowUpload(false)} />
      )}
    </div>
  );
}
